import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExperience } from '../../hooks/useexperience';
import { experienceService } from '../../services/dataService';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeft, Plus, Edit2, Trash2 } from 'lucide-react';

const AdminExperience = () => {
  const navigate = useNavigate();
  const { experience, loading, error, refetch } = useExperience();
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (id, role) => {
    if (window.confirm(`Delete "${role}"? This cannot be undone.`)) {
      setDeleting(id);
      try {
        const result = await experienceService.delete(id);
        if (result.success) {
          toast.success('Experience entry deleted successfully');
          refetch();
        } else {
          toast.error('Failed to delete experience entry');
        }
      } catch (error) {
        toast.error('Error deleting experience entry');
      } finally {
        setDeleting(null);
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />

      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/dashboard')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={() => navigate('/adminaccess/experience/new')}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Experience</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">Manage Experience</h1>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="p-8 rounded-lg border border-white/10 bg-white/5 text-center">
            <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
            <p className="text-white/60">Loading experience...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 rounded-lg border border-red-500/20 bg-red-500/5 text-center">
            <p className="text-red-400">Error: {error}</p>
          </div>
        )}

        {/* Experience List */}
        {!loading && !error && (
          <div className="space-y-4">
            {experience.length === 0 ? (
              <div className="p-12 rounded-lg border border-white/10 bg-white/5 text-center">
                <p className="text-white/40 mb-4">No experience entries yet</p>
                <button
                  onClick={() => navigate('/adminaccess/experience/new')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Your First Experience Entry</span>
                </button>
              </div>
            ) : (
              experience.map((exp) => (
                <div
                  key={exp.id}
                  className="p-4 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all"
                >
                  <div className="flex gap-4">
                    {/* Experience Image */}
                    {exp.image && (
                      <img
                        src={exp.image}
                        alt={exp.company}
                        className="w-20 h-20 rounded-lg object-cover border border-white/10"
                      />
                    )}

                    {/* Experience Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-medium mb-1">{exp.role}</h3>
                          <div className="text-white/60 mb-1">{exp.company}</div>
                          <div className="text-sm text-white/40 mb-2">{exp.period}</div>
                          {exp.description && (
                            <p className="text-sm text-white/50 leading-relaxed">{exp.description}</p>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/adminaccess/experience/edit/${exp.id}`)}
                            className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(exp.id, exp.role)}
                            disabled={deleting === exp.id}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminExperience;